import type { SightTapeOptions } from '@/types';

/**
 * Per-sight preset library.
 *
 * Tape widths reflect commonly-measured sight tape slot widths for each housing.
 * NOTE: even PCA admits they haven't physically measured every sight — these are
 * STARTING POINTS. The UI prompts the user to measure their own tape slot with
 * calipers and dial in the exact width.
 *
 * Range defaults: start 20yd, end 100yd, increment 5yd, hybrid OFF.
 * Adjust per-shooter in the customization panel — most archers want the tape
 * to extend to 80-120yd, ultra-long-range shooters out to 200yd.
 */
export const DEFAULT_TAPE_OPTIONS: SightTapeOptions = {
  tapeWidthInches: 0.50,
  startYards: 20,
  endYards: 100,
  incrementYards: 5,
  useHybridIncrement: false,
  hybridSwitchYards: 50,
  hybridFineIncrement: 1,
  majorWeight: 1.0,
  majorLengthFraction: 1.0,
  minorWeight: 0.5,
  minorLengthFraction: 0.5,
  fontSize: 8,
  fontBold: true,
  numberPosition: 'right',
  labelEveryYards: 10,
  cutOutlineStyle: 'dashed',
  presetName: 'Default',
};

export interface TapePreset {
  /** "Brand Model" prefix used to auto-select for a sight. */
  matchPrefix: string;
  label: string;
  /** Approximate measured tape slot width if known (informational, shown in UI). */
  measuredWidthNote?: string;
  options: SightTapeOptions;
}

const base = (presetName: string, widthInches: number, overrides: Partial<SightTapeOptions> = {}): SightTapeOptions => ({
  ...DEFAULT_TAPE_OPTIONS,
  tapeWidthInches: widthInches,
  presetName,
  ...overrides,
});

/**
 * Per-sight tape strip widths — verified or commonly-cited measurements.
 * The user can override; saved preference is stored on the Setup.
 */
export const SIGHT_PRESETS: TapePreset[] = [
  // ────────────────────────────────────────────────────────
  // ULTRAVIEW — only UV Slider 2 is a current standalone sight (verified
  // May 2026 vs ultraviewarchery.com). UV3/UV3XL are scope housings.
  // ────────────────────────────────────────────────────────
  {
    matchPrefix: 'Ultraview UV Slider 2',
    label: 'Ultraview UV Slider 2',
    measuredWidthNote: 'UVS2 vinyl tape slot ~0.45"; metal tape ~0.30". Measure your housing — UVS2 ships both styles.',
    options: base('Ultraview UV Slider 2', 0.45, { fontSize: 7 }),
  },

  // ────────────────────────────────────────────────────────
  // AXCEL — DRIVR uses NARROW metal tapes
  // ────────────────────────────────────────────────────────
  {
    matchPrefix: 'Axcel DRIVR',
    label: 'Axcel DRIVR (metal tape)',
    measuredWidthNote: 'DRIVR metal tape strip ~0.25" — very narrow',
    options: base('Axcel DRIVR', 0.25, {
      fontSize: 6,
      majorWeight: 1.2,
      minorWeight: 0.6,
      minorLengthFraction: 0.6,
    }),
  },
  {
    matchPrefix: 'Axcel Landslyde',
    label: 'Axcel Landslyde',
    measuredWidthNote: 'Landslyde tape slot ~0.50"',
    options: base('Axcel Landslyde', 0.50),
  },
  {
    matchPrefix: 'Axcel AccuTouch',
    label: 'Axcel AccuTouch',
    measuredWidthNote: 'AccuTouch tape slot ~0.50"',
    options: base('Axcel AccuTouch', 0.50),
  },
  {
    matchPrefix: 'Axcel AccuView',
    label: 'Axcel AccuView',
    measuredWidthNote: 'AccuView tape slot ~0.50"',
    options: base('Axcel AccuView', 0.50),
  },

  // ────────────────────────────────────────────────────────
  // SPOT HOGG — wider tape slots typical
  // ────────────────────────────────────────────────────────
  {
    matchPrefix: 'Spot Hogg Fast Eddie XL',
    label: 'Spot Hogg Fast Eddie XL',
    measuredWidthNote: 'Fast Eddie XL tape slot ~0.55"',
    options: base('Spot Hogg Fast Eddie XL', 0.55, {
      majorWeight: 1.2,
      minorWeight: 0.6,
    }),
  },
  {
    matchPrefix: 'Spot Hogg Fast Eddie',
    label: 'Spot Hogg Fast Eddie',
    measuredWidthNote: 'Fast Eddie tape slot ~0.55"',
    options: base('Spot Hogg Fast Eddie', 0.55, {
      majorWeight: 1.2,
      minorWeight: 0.6,
    }),
  },
  {
    matchPrefix: 'Spot Hogg Boss Hogg',
    label: 'Spot Hogg Boss Hogg',
    measuredWidthNote: 'Boss Hogg tape slot ~0.625"',
    options: base('Spot Hogg Boss Hogg', 0.625, {
      majorWeight: 1.2,
      minorWeight: 0.6,
    }),
  },
  {
    matchPrefix: 'Spot Hogg Hogg Father Pro',
    label: 'Spot Hogg Hogg Father Pro',
    measuredWidthNote: 'Hogg Father Pro tape slot ~0.33" (measured from PCA reference)',
    options: base('Spot Hogg Hogg Father Pro', 0.33, {
      majorWeight: 1.2,
      minorWeight: 0.4,
    }),
  },
  {
    matchPrefix: 'Spot Hogg Tommy Hogg',
    label: 'Spot Hogg Tommy Hogg',
    measuredWidthNote: 'Tommy Hogg tape slot ~0.55"',
    options: base('Spot Hogg Tommy Hogg', 0.55),
  },
  {
    matchPrefix: 'Spot Hogg Real Deal',
    label: 'Spot Hogg Real Deal',
    measuredWidthNote: 'Real Deal tape slot ~0.55"',
    options: base('Spot Hogg Real Deal', 0.55),
  },
  {
    matchPrefix: 'Spot Hogg Boonie',
    label: 'Spot Hogg Boonie',
    measuredWidthNote: 'Boonie tape slot ~0.55"',
    options: base('Spot Hogg Boonie', 0.55),
  },

  // ────────────────────────────────────────────────────────
  // BLACK GOLD
  // ────────────────────────────────────────────────────────
  {
    matchPrefix: 'Black Gold Ascent Whitetail',
    label: 'Black Gold Ascent Whitetail',
    measuredWidthNote: 'Ascent Whitetail tape slot ~0.50"',
    options: base('Black Gold Ascent Whitetail', 0.50),
  },
  {
    matchPrefix: 'Black Gold Ascent',
    label: 'Black Gold Ascent',
    measuredWidthNote: 'Ascent tape slot ~0.50"',
    options: base('Black Gold Ascent', 0.50),
  },
  {
    matchPrefix: 'Black Gold Pro Hunter',
    label: 'Black Gold Pro Hunter',
    measuredWidthNote: 'Pro Hunter tape slot ~0.50"',
    options: base('Black Gold Pro Hunter', 0.50),
  },
  {
    matchPrefix: 'Black Gold Pure 3',
    label: 'Black Gold Pure 3',
    measuredWidthNote: 'Pure 3 tape slot ~0.40"',
    options: base('Black Gold Pure 3', 0.40),
  },
  {
    matchPrefix: 'Black Gold Mojo 1810',
    label: 'Black Gold Mojo 1810',
    measuredWidthNote: 'Mojo 1810 tape slot ~0.40"',
    options: base('Black Gold Mojo 1810', 0.40),
  },

  // ────────────────────────────────────────────────────────
  // HHA — narrow tape strips
  // ────────────────────────────────────────────────────────
  {
    matchPrefix: 'HHA Tetra',
    label: 'HHA Tetra',
    measuredWidthNote: 'Tetra tape slot ~0.30"',
    options: base('HHA Tetra', 0.30, { fontSize: 7 }),
  },
  {
    matchPrefix: 'HHA Optimizer',
    label: 'HHA Optimizer',
    measuredWidthNote: 'Optimizer tape slot ~0.25" — very narrow',
    options: base('HHA Optimizer', 0.25, { fontSize: 6 }),
  },

  // ────────────────────────────────────────────────────────
  // IQ / CBE / Trophy Ridge / Sword
  // ────────────────────────────────────────────────────────
  {
    matchPrefix: 'IQ Define',
    label: 'IQ Define',
    measuredWidthNote: 'IQ Define tape slot ~0.30"',
    options: base('IQ Define', 0.30, { fontSize: 7 }),
  },
  {
    matchPrefix: 'IQ',
    label: 'IQ generic',
    measuredWidthNote: 'IQ tape slot ~0.30"',
    options: base('IQ', 0.30, { fontSize: 7 }),
  },
  {
    matchPrefix: 'CBE Engage Hybrid',
    label: 'CBE Engage Hybrid',
    measuredWidthNote: 'Engage Hybrid tape slot ~0.50"',
    options: base('CBE Engage Hybrid', 0.50),
  },
  {
    matchPrefix: 'CBE',
    label: 'CBE generic',
    options: base('CBE', 0.50),
  },
  {
    matchPrefix: 'Trophy Ridge',
    label: 'Trophy Ridge generic',
    options: base('Trophy Ridge', 0.40),
  },
  {
    matchPrefix: 'Sword',
    label: 'Sword generic',
    options: base('Sword', 0.50),
  },
];

/**
 * Find the preset for a sight by its "Brand Model" string.
 * Returns DEFAULT_TAPE_OPTIONS if no match found.
 */
export function presetForSight(sightBrand: string, sightModel: string): SightTapeOptions {
  const full = `${sightBrand} ${sightModel}`;
  const match = SIGHT_PRESETS.find((p) => full.startsWith(p.matchPrefix));
  return match ? { ...match.options } : { ...DEFAULT_TAPE_OPTIONS };
}

/**
 * Generate the yardage list for a given tape options config — applies the
 * hybrid increment switch if enabled. This is the single source of truth that
 * the preview, PDF, and mark table all read from.
 */
export function buildYardageList(opts: SightTapeOptions): number[] {
  const { startYards, endYards, incrementYards, useHybridIncrement, hybridSwitchYards, hybridFineIncrement } = opts;
  const start = Math.max(1, Math.min(startYards, endYards));
  const end = Math.min(250, Math.max(start, endYards));
  const baseInc = Math.max(0.5, incrementYards);
  const fineInc = Math.max(0.5, hybridFineIncrement);
  const switchAt = useHybridIncrement ? Math.max(start, Math.min(end, hybridSwitchYards)) : end + 1;

  const out: number[] = [];
  // Always include start
  out.push(start);
  // Phase 1: start → switchAt with base increment
  let y = start + baseInc;
  while (y <= switchAt + 1e-6 && y <= end + 1e-6) {
    out.push(Math.round(y * 10) / 10);
    y = +(y + baseInc).toFixed(6);
  }
  // Phase 2: past switchAt → end with fine increment
  if (useHybridIncrement && switchAt < end) {
    let y2 = Math.max(switchAt + fineInc, out[out.length - 1] + fineInc);
    while (y2 <= end + 1e-6) {
      out.push(Math.round(y2 * 10) / 10);
      y2 = +(y2 + fineInc).toFixed(6);
    }
  }
  // Always include endYards if integer-aligned and not already in list
  if (out[out.length - 1] < end - 1e-6) out.push(end);

  // Dedupe + sort (in case of float fuzz)
  const seen = new Set<number>();
  return out
    .filter((v) => {
      const k = Math.round(v * 10);
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    })
    .sort((a, b) => a - b);
}
